// src/components/ICT/UserManagement.jsx

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  TextField,
  Alert,
  Snackbar,
  Fade,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Tooltip,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Restaurant as RestaurantIcon,
  Storefront as StoreIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Computer as ComputerIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';

// Import local components
import ICTHeader from '../../components/ICT/Header';
import ICTSidebar from '../../components/ICT/Sidebar';
import ICTFooter from '../../components/ICT/Footer';
import Preloader from '../../theme/Preloader';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getInitials = (firstName, otherNames = '') => {
  const name = `${firstName} ${otherNames}`.trim();
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getRoleColor = (role) => {
  const colors = {
    admin: '#F44336',
    manager: '#1976D2',
    cashier: '#2E7D32',
    waiter: '#FF9800',
    account: '#9C27B0',
    ict: '#00BCD4',
    kitchen: '#FF5722',
    staff: '#607D8B',
    store_keeper: '#8BC34A',
    auditor: '#3F51B5',
    hr: '#E91E63',
  };
  return colors[role?.toLowerCase()] || '#757575';
};

const getRoleIcon = (role) => {
  const icons = {
    admin: <AdminIcon sx={{ fontSize: 16 }} />,
    manager: <BadgeIcon sx={{ fontSize: 16 }} />,
    cashier: <PaymentIcon sx={{ fontSize: 16 }} />,
    waiter: <RestaurantIcon sx={{ fontSize: 16 }} />,
    account: <PeopleIcon sx={{ fontSize: 16 }} />,
    ict: <ComputerIcon sx={{ fontSize: 16 }} />,
    kitchen: <RestaurantIcon sx={{ fontSize: 16 }} />,
    staff: <PersonIcon sx={{ fontSize: 16 }} />,
    store_keeper: <StoreIcon sx={{ fontSize: 16 }} />,
    auditor: <AssessmentIcon sx={{ fontSize: 16 }} />,
    hr: <PeopleIcon sx={{ fontSize: 16 }} />,
  };
  return icons[role?.toLowerCase()] || <PersonIcon sx={{ fontSize: 16 }} />;
};

// ============================================
// GENERATE RANDOM STAFF ID
// ============================================

const generateStaffId = () => {
  // Generate a random staff ID with pattern: STF-XXXXX
  const randomNum = Math.floor(10000 + Math.random() * 90000);
  return `STF-${randomNum}`;
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[UserManagement] Error checking token expiry:', err);
    return true;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[UserManagement] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[UserManagement] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[UserManagement] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[UserManagement] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/ICT_LoginPage';
    return null;
  }
};

// ============================================
// API HELPERS
// ============================================

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[UserManagement] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  let url = `${FULL_API_URL}${endpoint}`;
  
  if (options.params) {
    const params = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  console.debug(`[UserManagement] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { 
      ...options, 
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined
    });

    if (response.status === 401) {
      console.debug('[UserManagement] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    // ✅ FIX: Handle 204 No Content (DELETE responses)
    if (response.status === 204) {
      console.debug('[UserManagement] No content response (204)');
      return null;
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      // If response is empty, return null
      if (!text || text.trim() === '') {
        return null;
      }
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[UserManagement] API Error:', err.message);
    throw err;
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const UserManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [activeMenu, setActiveMenu] = useState('users');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  
  // User data
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  
  // Dropdown options
  const [locations, setLocations] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  
  // Dialog states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    staff_id: generateStaffId(), // Auto-generate on init
    first_name: '',
    other_names: '',
    department: '',
    phone: '',
    email: '',
    gender: '',
    role: '',
    location: '',
    password: '',
    confirm_password: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================
  // REFS
  // ============================================
  
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // ============================================
  // DATA FETCHING
  // ============================================
  
  const fetchUsers = useCallback(async () => {
    try {
      console.debug('[UserManagement] Fetching users...');
      setIsFetching(true);
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      
      if (searchQuery) params.search = searchQuery;
      if (roleFilter) params.role = roleFilter;
      if (locationFilter) params.location = locationFilter;
      
      const data = await apiRequest('/users', { params });
      console.debug('[UserManagement] Users fetched:', data);
      
      if (isMountedRef.current) {
        setUsers(data.users || []);
        setTotalUsers(data.total || 0);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.debug('[UserManagement] Request aborted');
        return;
      }
      console.error('[UserManagement] Error fetching users:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch users: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [page, rowsPerPage, searchQuery, roleFilter, locationFilter]);

  const fetchDropdownData = useCallback(async () => {
    try {
      console.debug('[UserManagement] Fetching dropdown data...');
      
      const [locationsData, rolesData, deptsData] = await Promise.all([
        apiRequest('/locations'),
        apiRequest('/roles'),
        apiRequest('/departments'),
      ]);
      
      console.debug('[UserManagement] Dropdown data fetched:', { locationsData, rolesData, deptsData });
      
      if (isMountedRef.current) {
        setLocations(locationsData || []);
        setRoleOptions(rolesData || []);
        setDepartmentOptions(deptsData || []);
      }
    } catch (err) {
      console.error('[UserManagement] Error fetching dropdown data:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    await Promise.all([
      fetchUsers(),
      fetchDropdownData(),
    ]);
  }, [fetchUsers, fetchDropdownData]);

  // ============================================
  // EFFECTS
  // ============================================
  
  // Initial load
  useEffect(() => {
    isMountedRef.current = true;
    
    console.debug('[UserManagement] Initializing User Management...');
    
    loadData();
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }, 600);
    
    return () => {
      clearTimeout(timer);
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load on filter/pagination change
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        fetchUsers();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [page, rowsPerPage, roleFilter, locationFilter, isLoading, fetchUsers]);

  // Debounced search
  useEffect(() => {
    if (!isLoading) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        fetchUsers();
      }, 500);
      return () => clearTimeout(searchTimeoutRef.current);
    }
  }, [searchQuery, isLoading, fetchUsers]);

  // ============================================
  // HANDLERS
  // ============================================
  
  const handleRefresh = useCallback(async () => {
    console.debug('[UserManagement] Manual refresh triggered');
    await loadData();
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully!',
      severity: 'success',
    });
  }, [loadData]);

  const handleOpenCreateDialog = useCallback(() => {
    console.debug('[UserManagement] Opening create user dialog');
    setEditingUser(null);
    setFormData({
      staff_id: generateStaffId(), // Generate new staff ID
      first_name: '',
      other_names: '',
      department: '',
      phone: '',
      email: '',
      gender: '',
      role: '',
      location: '',
      password: '',
      confirm_password: '',
    });
    setFormErrors({});
    setShowPassword(false);
    setUserDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((user) => {
    console.debug(`[UserManagement] Opening edit dialog for user: ${user.email}`);
    setEditingUser(user);
    setFormData({
      staff_id: user.staff_id || '',
      first_name: user.first_name || '',
      other_names: user.other_names || '',
      department: user.department || '',
      phone: user.phone || '',
      email: user.email || '',
      gender: user.gender || '',
      role: user.role || '',
      location: user.location || '',
      password: '',
      confirm_password: '',
    });
    setFormErrors({});
    setShowPassword(false);
    setUserDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setUserDialogOpen(false);
    setEditingUser(null);
    setFormErrors({});
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    const isEditing = !!editingUser;
    
    if (!formData.staff_id.trim()) {
      errors.staff_id = 'Staff ID is required';
    }
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!formData.department) {
      errors.department = 'Department is required';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s()]{10,20}$/.test(formData.phone)) {
      errors.phone = 'Invalid phone number format';
    }
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.gender) {
      errors.gender = 'Gender is required';
    }
    if (!formData.role) {
      errors.role = 'Role is required';
    }
    
    // Password validation (only for new users or when password is provided)
    if (!isEditing || formData.password) {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      } else if (!/\d/.test(formData.password)) {
        errors.password = 'Password must contain at least one digit';
      } else if (!/[A-Z]/.test(formData.password)) {
        errors.password = 'Password must contain at least one uppercase letter';
      } else if (!/[a-z]/.test(formData.password)) {
        errors.password = 'Password must contain at least one lowercase letter';
      }
      
      if (formData.password && formData.password !== formData.confirm_password) {
        errors.confirm_password = 'Passwords do not match';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, editingUser]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      console.debug('[UserManagement] Form validation failed');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const userData = {
        staff_id: formData.staff_id.trim(),
        first_name: formData.first_name.trim(),
        other_names: formData.other_names.trim() || null,
        department: formData.department,
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        gender: formData.gender,
        role: formData.role,
        location: formData.location || null,
        password: formData.password,
        confirm_password: formData.confirm_password,
      };
      
      let response;
      
      if (editingUser) {
        console.debug(`[UserManagement] Updating user: ${editingUser.id}`);
        // Remove password fields if not changing
        if (!formData.password) {
          delete userData.password;
          delete userData.confirm_password;
        }
        response = await apiRequest(`/users/${editingUser.id}`, {
          method: 'PUT',
          body: userData
        });
        setSnackbar({
          open: true,
          message: 'User updated successfully!',
          severity: 'success',
        });
      } else {
        console.debug('[UserManagement] Creating new user');
        response = await apiRequest('/users', {
          method: 'POST',
          body: userData
        });
        setSnackbar({
          open: true,
          message: 'User created successfully!',
          severity: 'success',
        });
      }
      
      console.debug('[UserManagement] User operation successful:', response);
      
      handleCloseDialog();
      await fetchUsers();
    } catch (err) {
      console.error('[UserManagement] Error saving user:', err);
      setSnackbar({
        open: true,
        message: `Failed to save user: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, editingUser, validateForm, handleCloseDialog, fetchUsers]);

  const handleDeleteClick = useCallback((user) => {
    console.debug(`[UserManagement] Delete dialog for user: ${user.email}`);
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) return;
    
    try {
      console.debug(`[UserManagement] Deleting user: ${userToDelete.id}`);
      // ✅ FIX: DELETE returns 204 No Content, so we handle null response
      const response = await apiRequest(`/users/${userToDelete.id}`, {
        method: 'DELETE'
      });
      
      // ✅ Response is null for 204, which is success
      console.debug('[UserManagement] Delete successful:', response);
      
      setSnackbar({
        open: true,
        message: 'User deleted successfully!',
        severity: 'success',
      });
      
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await fetchUsers();
    } catch (err) {
      console.error('[UserManagement] Error deleting user:', err);
      setSnackbar({
        open: true,
        message: `Failed to delete user: ${err.message}`,
        severity: 'error',
      });
    }
  }, [userToDelete, fetchUsers]);

  const handleToggleActive = useCallback(async (user) => {
    try {
      console.debug(`[UserManagement] Toggling user status: ${user.id}`);
      const response = await apiRequest(`/users/${user.id}/toggle-active`, {
        method: 'POST'
      });
      
      setSnackbar({
        open: true,
        message: response.message || 'User status updated successfully!',
        severity: 'success',
      });
      
      await fetchUsers();
    } catch (err) {
      console.error('[UserManagement] Error toggling user status:', err);
      setSnackbar({
        open: true,
        message: `Failed to update user status: ${err.message}`,
        severity: 'error',
      });
    }
  }, [fetchUsers]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  
  const renderStatsCards = useCallback(() => {
    const total = totalUsers;
    const active = users.filter(u => u.is_active).length;
    const inactive = total - active;
    
    const roles = users.reduce((acc, u) => {
      acc[u.role] = (acc[u.role] || 0) + 1;
      return acc;
    }, {});
    const uniqueRoles = Object.keys(roles).length;
    
    const stats = [
      { label: 'Total Users', value: total, icon: <PeopleIcon />, color: '#1976D2' },
      { label: 'Active Users', value: active, icon: <CheckCircleIcon />, color: '#2E7D32' },
      { label: 'Inactive Users', value: inactive, icon: <CancelIcon />, color: '#F44336' },
      { label: 'Roles', value: uniqueRoles, icon: <BadgeIcon />, color: '#FF9800' },
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2.5 },
                borderRadius: '14px',
                border: `1px solid ${alpha(stat.color, 0.15)}`,
                bgcolor: alpha(stat.color, 0.05),
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: theme.shadows[3],
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(stat.color, 0.15),
                  color: stat.color,
                  width: { xs: 36, sm: 48 },
                  height: { xs: 36, sm: 48 },
                }}
              >
                {stat.icon}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  fontWeight={600} 
                  sx={{ 
                    fontSize: { xs: '0.55rem', sm: '0.8rem' },
                    display: 'block',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight={700} 
                  sx={{ 
                    fontSize: { xs: '0.85rem', sm: '1.2rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {stat.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }, [users, totalUsers, theme]);

  const renderFilters = useCallback(() => {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: '14px',
          mb: 3,
          border: `1px solid ${alpha('#000', 0.06)}`,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 },
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flex: { xs: 1, sm: 2 },
            minWidth: { xs: '100%', sm: '200px' },
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: '160px' }, flex: 1 }}>
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            displayEmpty
            sx={{
              borderRadius: '10px',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              '& .MuiSelect-select': {
                py: { xs: 0.75, sm: 1 },
              },
            }}
          >
            <MenuItem value="">All Roles</MenuItem>
            {roleOptions.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: '160px' }, flex: 1 }}>
          <Select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            displayEmpty
            sx={{
              borderRadius: '10px',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              '& .MuiSelect-select': {
                py: { xs: 0.75, sm: 1 },
              },
            }}
          >
            <MenuItem value="">All Locations</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.name}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            px: { xs: 2, sm: 3 },
            py: { xs: 0.75, sm: 1 },
            whiteSpace: 'nowrap',
            bgcolor: '#1976D2',
            '&:hover': { bgcolor: '#1565C0' },
          }}
        >
          Add User
        </Button>
        
        <Button
          variant="outlined"
          startIcon={isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={isFetching}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.75, sm: 1 },
            whiteSpace: 'nowrap',
            borderColor: '#1976D2',
            color: '#1976D2',
            '&:hover': {
              borderColor: '#1565C0',
              bgcolor: alpha('#1976D2', 0.08),
            },
          }}
        >
          {isFetching ? 'Loading...' : 'Refresh'}
        </Button>
      </Paper>
    );
  }, [searchQuery, roleFilter, locationFilter, locations, roleOptions, isFetching, handleOpenCreateDialog, handleRefresh]);

  const renderUsersTable = useCallback(() => {
    if (isFetching && users.length === 0) {
      return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '14px' }}>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1, borderRadius: '8px' }} />
          ))}
        </Paper>
      );
    }

    if (users.length === 0) {
      return (
        <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: '14px', textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
            No users found. Try adjusting your filters or create a new user.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper sx={{ borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800, minWidth: 100 }}>User</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800, minWidth: 100 }}>Staff ID</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800, minWidth: 140 }}>Contact</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800, minWidth: 120 }}>Department</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800, minWidth: 100 }}>Role</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800, minWidth: 100 }}>Location</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800, minWidth: 90 }}>Status</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800, minWidth: 120, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const roleColor = getRoleColor(user.role);
                const initials = getInitials(user.first_name, user.other_names);
                
                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: { xs: 32, sm: 40 },
                            height: { xs: 32, sm: 40 },
                            bgcolor: alpha(roleColor, 0.12),
                            color: roleColor,
                            fontSize: { xs: '0.7rem', sm: '0.85rem' },
                            fontWeight: 700,
                          }}
                        >
                          {initials}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {user.first_name} {user.other_names || ''}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: '0.6rem', sm: '0.75rem' },
                              display: 'block',
                            }}
                          >
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.staff_id}
                        sx={{
                          fontSize: { xs: '0.6rem', sm: '0.75rem' },
                          fontWeight: 700,
                          bgcolor: alpha('#1976D2', 0.08),
                          color: '#1976D2',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: { xs: '0.6rem', sm: '0.75rem' },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          {user.phone}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: '0.55rem', sm: '0.7rem' },
                            display: 'block',
                          }}
                        >
                          Joined: {formatDate(user.created_at)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.department}
                        sx={{
                          fontSize: { xs: '0.55rem', sm: '0.7rem' },
                          fontWeight: 600,
                          bgcolor: alpha('#9C27B0', 0.08),
                          color: '#9C27B0',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.role}
                        sx={{
                          fontSize: { xs: '0.55rem', sm: '0.7rem' },
                          fontWeight: 700,
                          bgcolor: alpha(roleColor, 0.12),
                          color: roleColor,
                        }}
                        icon={getRoleIcon(user.role)}
                      />
                    </TableCell>
                    <TableCell>
                      {user.location ? (
                        <Chip
                          size="small"
                          label={user.location}
                          sx={{
                            fontSize: { xs: '0.55rem', sm: '0.7rem' },
                            fontWeight: 600,
                            bgcolor: alpha('#4CAF50', 0.08),
                            color: '#4CAF50',
                          }}
                          icon={<LocationIcon sx={{ fontSize: 16 }} />}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={user.is_active}
                            onChange={() => handleToggleActive(user)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#2E7D32',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                bgcolor: '#2E7D32',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: { xs: '0.55rem', sm: '0.7rem' },
                              fontWeight: 700,
                              color: user.is_active ? '#2E7D32' : '#F44336',
                            }}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Typography>
                        }
                        sx={{ mr: 0 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(user)}
                            sx={{
                              color: '#1976D2',
                              '&:hover': { bgcolor: alpha('#1976D2', 0.08) },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(user)}
                            sx={{
                              color: '#F44336',
                              '&:hover': { bgcolor: alpha('#F44336', 0.08) },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.6rem', sm: '0.875rem' },
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.55rem', sm: '0.875rem' },
            },
            '& .MuiTablePagination-actions button': {
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
            },
          }}
        />
      </Paper>
    );
  }, [users, totalUsers, page, rowsPerPage, isFetching, handleToggleActive, handleOpenEditDialog, handleDeleteClick, handleChangePage, handleChangeRowsPerPage]);

  // ============================================
  // DIALOGS
  // ============================================
  
  const renderUserDialog = useCallback(() => {
    const isEditing = !!editingUser;
    const title = isEditing ? 'Edit User' : 'Create New User';
    
    return (
      <Dialog
        open={userDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
            p: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: alpha('#1976D2', 0.04),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon sx={{ color: '#1976D2' }} />
            <Typography 
              variant="h6" 
              component="span"  
              fontWeight={800} 
              sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}
            >
              {title}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Staff ID"
                value={formData.staff_id}
                onChange={(e) => handleFormChange('staff_id', e.target.value)}
                error={!!formErrors.staff_id}
                helperText={formErrors.staff_id}
                required
                disabled={!isEditing} 
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  },
                }}
              />
              {!isEditing && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Staff ID is auto-generated and cannot be changed
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Other Names"
                value={formData.other_names}
                onChange={(e) => handleFormChange('other_names', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!formErrors.gender}>
                <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  onChange={(e) => handleFormChange('gender', e.target.value)}
                  label="Gender"
                  sx={{
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
                {formErrors.gender && (
                  <FormHelperText error>{formErrors.gender}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!formErrors.department}>
                <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Department</InputLabel>
                <Select
                  value={formData.department}
                  onChange={(e) => handleFormChange('department', e.target.value)}
                  label="Department"
                  sx={{
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  {departmentOptions.map((dept) => (
                    <MenuItem key={dept.value} value={dept.value}>
                      {dept.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.department && (
                  <FormHelperText error>{formErrors.department}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!formErrors.role}>
                <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  label="Role"
                  sx={{
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.role && (
                  <FormHelperText error>{formErrors.role}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Location</InputLabel>
                <Select
                  value={formData.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  label="Location"
                  sx={{
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  <MenuItem value="">No Location</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.name}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {(formData.role === 'cashier' || formData.role === 'waiter') && !formData.location && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    ⚠️ Cashiers and Waiters require a location to access the system.
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                {isEditing ? 'Reset Password (leave blank to keep current)' : 'Set Password'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password}
                required={!isEditing}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => handleFormChange('confirm_password', e.target.value)}
                error={!!formErrors.confirm_password}
                helperText={formErrors.confirm_password}
                required={!isEditing}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          gap: 1,
          flexWrap: 'wrap',
        }}>
          <Button
            onClick={handleCloseDialog}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              bgcolor: '#1976D2',
              '&:hover': { bgcolor: '#1565C0' },
              '&:disabled': { bgcolor: 'grey.300' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: { xs: 3, sm: 4 },
              py: { xs: 1, sm: 1.25 },
              fontSize: { xs: '0.75rem', sm: '0.95rem' },
            }}
            startIcon={isSubmitting ? <CircularProgress size={22} color="inherit" /> : (isEditing ? <EditIcon /> : <PersonAddIcon />)}
          >
            {isSubmitting ? 'Saving...' : (isEditing ? 'Update User' : 'Create User')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [userDialogOpen, editingUser, formData, formErrors, isSubmitting, showPassword, locations, roleOptions, departmentOptions, handleCloseDialog, handleFormChange, handleSubmit]);

  const renderDeleteDialog = useCallback(() => {
    if (!userToDelete) return null;
    
    return (
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: alpha('#F44336', 0.04),
        }}>
          <CancelIcon sx={{ color: '#F44336' }} />
          <Typography variant="h6" component="span" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
            Delete User
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
              Are you sure you want to delete this user?
            </Typography>
          </Alert>
          
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: '12px',
              bgcolor: alpha('#000', 0.02),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: alpha('#F44336', 0.12),
                  color: '#F44336',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                {getInitials(userToDelete.first_name, userToDelete.other_names)}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={800} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  {userToDelete.first_name} {userToDelete.other_names || ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                  {userToDelete.email} • {userToDelete.role}
                </Typography>
              </Box>
            </Box>
          </Paper>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
            This action will soft-delete the user (set to inactive). The user data will be preserved but they won't be able to log in.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          gap: 1,
          flexWrap: 'wrap',
        }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: 4,
              py: 1.25,
              fontSize: { xs: '0.75rem', sm: '0.95rem' },
            }}
            startIcon={<DeleteIcon />}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [deleteDialogOpen, userToDelete, handleConfirmDelete]);

  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return <Preloader text="Loading User Management..." size={70} fullScreen />;
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <ICTSidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
        
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <ICTHeader />
          
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            p: { xs: 1.5, sm: 2, md: 3 },
            bgcolor: '#f8fafc',
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.300',
              borderRadius: 3,
            },
          }}>
            {/* Header Section */}
            <Box sx={{
              bgcolor: 'white',
              borderRadius: '16px',
              p: { xs: 1.5, sm: 2.5, md: 3 },
              mb: { xs: 1.5, sm: 2.5, md: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexWrap: 'wrap',
              gap: 2,
              border: `1px solid ${alpha('#1976D2', 0.12)}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <Box>
                <Typography variant="h4" sx={{
                  color: '#1976D2',
                  fontWeight: 800,
                  fontSize: { xs: '1.4rem', sm: '2rem', md: '2.4rem' },
                }}>
                  👥 User Management
                  <span style={{
                    display: 'inline-block',
                    width: '4px',
                    height: { xs: '28px', sm: '36px', md: '40px' },
                    backgroundColor: '#1976D2',
                    marginLeft: '8px',
                    verticalAlign: 'text-bottom',
                    animation: 'blink 1s step-end infinite',
                  }} />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }, fontWeight: 600 }}>
                  Manage system users, roles, and permissions
                </Typography>
              </Box>
            </Box>

            {/* Stats Cards */}
            {renderStatsCards()}

            {/* Filters */}
            {renderFilters()}

            {/* Users Table */}
            {renderUsersTable()}

            {/* Footer Info */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Total users: {totalUsers} • Showing {users.length} users
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <ICTFooter />
      
      {/* Dialogs */}
      {renderUserDialog()}
      {renderDeleteDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
};

export default UserManagement;